import type { ChatToolCall, ToolResult } from './McpAgentTools.ts'

/** Provider-native reasoning attached to an assistant turn (Anthropic `thinking`
 * blocks, OpenAI Responses `reasoning` items). Rendered as collapsible thinking. */
export interface ChatReasoning {
  text?: string
  redacted?: boolean
}

export interface GenerativeChatMessage {
  role?: string
  content?: string
  // Party that authored a replayed history turn: the connecting counterparty for
  // user turns, the owner for assistant/tool turns. Absent on live send events.
  party?: string
  toolCalls?: ChatToolCall[]
  toolResults?: ToolResult[]
  reasoning?: ChatReasoning[]
  metadata?: Record<string, unknown>
}

/**
 * A render-ready transcript event. Both the live `/send` conversation result and
 * a reloaded history (via {@link historyToRenderEvents}) produce this same shape,
 * so an app renders both through one loop.
 */
export interface GenerativeChatRenderEvent {
  role: 'user' | 'assistant' | 'tool' | 'tool-result' | 'thinking'
  content: string
  error?: boolean
}

/**
 * NDJSON frame streamed from `/send` and `/client-tool-results` when the caller
 * opts into streaming (one JSON object per line):
 * - `event`: one render event, emitted the moment the agent produces it.
 * - `done`: terminal frame, carrying any `clientToolCalls` the round left pending
 *   (the `events` are already on the wire, so they are not repeated here).
 * - `error`: the conversation threw mid-stream; the partial trail already streamed.
 */
export type GenerativeChatStreamFrame =
  | { type: 'event'; event: GenerativeChatRenderEvent }
  | { type: 'done'; clientToolCalls?: ChatToolCall[] }
  | { type: 'error'; message: string }

/** Content type for the NDJSON streaming responses, and the `Accept` value a
 * client sends to opt in. */
export const RENDER_STREAM_CONTENT_TYPE = 'application/x-ndjson'

/**
 * A render-event array that also notifies a listener on every `push`. The
 * conversation loop mutates one `GenerativeChatRenderEvent[]` in place via the
 * event builders ({@link appendAssistantEvent} et al.) and direct `push`es;
 * substituting this subclass makes each of those emit to the stream as a side
 * effect, with zero changes to the loop. The array still accumulates, so the
 * terminal frame and the non-streaming fallback keep the full list.
 */
export class RenderEventStream extends Array<GenerativeChatRenderEvent> {
  #onEvent?: (event: GenerativeChatRenderEvent) => void

  constructor(onEvent?: (event: GenerativeChatRenderEvent) => void) {
    super()
    this.#onEvent = onEvent
  }

  // Keep derived methods (map/filter/slice) returning plain arrays so they
  // don't re-enter this constructor with a length argument.
  static override get [Symbol.species](): ArrayConstructor {
    return Array
  }

  override push(...events: GenerativeChatRenderEvent[]): number {
    for (const event of events) this.#onEvent?.(event)
    return super.push(...events)
  }
}

/** Options shared by the history converter and the live event builders. */
export interface RenderEventOptions {
  /** Emit `tool` / `tool-result` events. Mirrors the live `toolDetails` flag. */
  toolDetails?: boolean
  /** Tool names that run in the browser, used only to label `tool` events. */
  clientToolNames?: Set<string>
}

/**
 * Expand the assistant turn: its native reasoning (as collapsible `thinking`
 * events ahead of the answer) and its text content. Tool-call events are added
 * separately by the caller, since live and history source them differently.
 */
export function appendAssistantEvent(
  events: GenerativeChatRenderEvent[],
  message: GenerativeChatMessage,
): void {
  // Native reasoning blocks render as collapsible `thinking` ahead of the answer.
  appendReasoningEvents(events, message.reasoning)
  if (message.content) {
    events.push({ role: 'assistant', content: message.content })
  } else if (!message.toolCalls?.length) {
    events.push({ role: 'assistant', content: '' })
  }
}

/**
 * Emit a turn's provider-native reasoning ({@link GenerativeChatMessage.reasoning})
 * as collapsible `thinking` render events. Redacted and empty blocks have no
 * displayable text and are skipped.
 */
export function appendReasoningEvents(
  events: GenerativeChatRenderEvent[],
  reasoning: ChatReasoning[] | undefined,
): void {
  for (const block of reasoning ?? []) {
    if (block.redacted || !block.text) continue
    events.push({ role: 'thinking', content: block.text })
  }
}

export function toolCallMessage(
  call: ChatToolCall,
  isClientTool: boolean,
): string {
  const label = isClientTool ? 'Client tool call' : 'Agent tool call'
  return `${label}: ${call.name}\n${JSON.stringify(call.input, null, 2)}`
}

export function appendToolResultEvent(
  events: GenerativeChatRenderEvent[],
  name: string,
  content: string,
  toolDetails: boolean,
  isError = false,
): void {
  if (!toolDetails) return
  events.push({
    role: 'tool-result',
    content: toolResultMessage(name, content, isError),
    ...(isError ? { error: true } : {}),
  })
}

export function toolResultMessage(
  name: string,
  content: string,
  isError = false,
): string {
  const label = isError ? 'Tool error' : 'Tool result'
  return `${label}: ${name}\n${content}`
}

/**
 * Convert a persisted chat history (`GenerativeChatMessage[]`, as returned on a
 * session's `history`) into the same flat {@link GenerativeChatRenderEvent} stream
 * the live `/send` path produces — so a reloaded transcript renders identically to
 * the live one (reasoning as `thinking`, tool calls/results as `tool` /
 * `tool-result`). Reuses the live formatters so the two never drift.
 *
 * - `user` turns become a `user` event (empty-content turns, e.g. the tool-result
 *   carrier, are skipped here and surfaced from the following `tool` turn).
 * - `assistant` turns expand to `thinking` + `assistant` + a `tool` event per
 *   `toolCalls` entry (tool events gated by `toolDetails`).
 * - `tool` turns expand to a `tool-result` event per `toolResults` entry (gated by
 *   `toolDetails`).
 * - `system` and unrecognised turns are skipped — they are not render events.
 */
export function historyToRenderEvents(
  messages: GenerativeChatMessage[],
  options: RenderEventOptions = {},
): GenerativeChatRenderEvent[] {
  const toolDetails = options.toolDetails ?? true
  const clientToolNames = options.clientToolNames
  const events: GenerativeChatRenderEvent[] = []

  for (const message of messages) {
    if (message.role === 'assistant') {
      appendAssistantEvent(events, message)
      if (toolDetails) {
        for (const call of message.toolCalls ?? []) {
          events.push({
            role: 'tool',
            content: toolCallMessage(
              call,
              clientToolNames?.has(call.name) ?? false,
            ),
          })
        }
      }
    } else if (message.role === 'tool') {
      for (const result of message.toolResults ?? []) {
        appendToolResultEvent(
          events,
          result.name ?? 'tool',
          result.content,
          toolDetails,
          result.isError,
        )
      }
    } else if (message.role === 'user') {
      if (message.content) {
        events.push({ role: 'user', content: message.content })
      }
    }
    // `system` and any other role carry no render event.
  }

  return events
}
